import type { Language } from '@/i18n/types';
import type { Translations } from '@/i18n/types';

/**
 * Language configuration with code and native name
 */
interface LanguageConfig {
  code: Language;
  /** The language name in its native form (e.g., "Español" for Spanish) */
  nativeName: string;
  /** Function to get the localized label from translations */
  getLabel: (t: Translations) => string;
}

/**
 * All supported languages in the application.
 * This is the single source of truth for available languages.
 */
export const LANGUAGES: LanguageConfig[] = [
  {
    code: 'en',
    nativeName: 'English',
    getLabel: (t) => t.settings.language.english,
  },
  {
    code: 'fr',
    nativeName: 'Français',
    getLabel: (t) => t.settings.language.french,
  },
  {
    code: 'de',
    nativeName: 'Deutsch',
    getLabel: (t) => t.settings.language.german,
  },
  {
    code: 'es',
    nativeName: 'Español',
    getLabel: (t) => t.settings.language.spanish,
  },
];

/**
 * Get the native name for a language code
 */
export function getLanguageNativeName(code: Language): string {
  const lang = LANGUAGES.find((l) => l.code === code);
  return lang?.nativeName ?? code;
}

/**
 * Get the localized label for a language code
 */
export function getLanguageLabel(code: Language, t: Translations): string {
  const lang = LANGUAGES.find((l) => l.code === code);
  return lang?.getLabel(t) ?? code;
}
